import React, { useState } from "react";
import { BASE_URL } from "@/lib/API";
import { DetailsWorkspaceModal, TableActionButton } from "../../../../components/ui";
import { Caption } from "@/components/ui/Typography";
import { Calendar, User, Eye, Phone, Activity, FileText, MessageSquare } from "lucide-react";
import { fmtDate } from "@/lib/formatters";
import { SiteVisitMediaPreviewModal } from "./SiteVisitMediaPreviewModal";

export const ViewSiteVisitModal = ({ isOpen, onClose, lead }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!lead) return null;

  const siteVisitData = lead.siteVisitData || {};
  const displayName = lead.customerName || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Lead";
  const lastNote = siteVisitData.commonNotes || "No notes yet";

  // Aggregate all photos across measurements with context
  const allMedia = [];
  siteVisitData.measurements?.forEach(meas => {
    if (meas.photos && Array.isArray(meas.photos)) {
      meas.photos.forEach(photo => {
        allMedia.push({
          ...photo,
          fileRaw: photo,
          room: meas.room,
          particulars: meas.particulars
        });
      });
    }
  });

  // Add site photos to allMedia
  if (siteVisitData.sitePhotos && Array.isArray(siteVisitData.sitePhotos)) {
    siteVisitData.sitePhotos.forEach(photo => {
      allMedia.push({
        ...photo,
        fileRaw: photo,
        room: "Site Photos",
        particulars: "General"
      });
    });
  }

  // Group media by room
  const groupedMedia = allMedia.reduce((acc, item) => {
    const room = item.room || "Other";
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  const fields = [
    { icon: User, label: "Verve ID", value: (lead.customId || "").toUpperCase() },
    { icon: User, label: "Client Name", value: displayName },
    { icon: FileText, label: "Media Count", value: `${allMedia.length} Files` },
    { icon: MessageSquare, label: "Site Visit Note", value: lastNote }
  ];

  const handlePreview = (index) => {
    setPreviewIndex(index);
    setIsPreviewOpen(true);
  };

  let globalMediaIdx = 0;

  return (
    <>
      <DetailsWorkspaceModal
        isOpen={isOpen}
        onClose={onClose}
        title="VERVE KITCHENS"
        subtitle="SITE VISIT DETAILS"
        fields={fields}
        hideSidebar={true}
        maxWidthClass="max-w-[1100px]"
        closeIcon="minimize"
      >
        <div className="flex flex-col gap-8">
          
          {/* Site Photos Block */}
          {siteVisitData.sitePhotos?.length > 0 && (
            <div className="flex flex-col">
              <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
                Site Photos
              </h4>
              <div className="flex flex-wrap gap-3">
                {siteVisitData.sitePhotos.map((photoObj, pIdx) => {
                  const fileId = photoObj.fileId || photoObj._id;
                  const fileUrl = `${BASE_URL}/api/verve-leads/site-visit/media/${fileId}`;
                  const globalIdx = allMedia.findIndex(media => {
                    const mId = typeof media.fileRaw === 'string' ? media.fileRaw : (media.fileRaw.fileId || media.fileRaw._id);
                    return mId === fileId;
                  });
                  return (
                    <div 
                      key={pIdx} 
                      onClick={() => globalIdx !== -1 && handlePreview(globalIdx)}
                      className="w-20 h-20 rounded-md border border-slate-200 overflow-hidden cursor-pointer hover:border-brand-blue hover:shadow-md transition-all bg-white flex items-center justify-center shrink-0"
                      title="View Photo"
                    >
                      <img src={fileUrl} alt="Site Photo" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attached Documents Block */}
          {siteVisitData.pdfDocuments?.length > 0 && (
            <div className="flex flex-col">
              <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
                Attached Documents
              </h4>
              <div className="flex flex-col gap-2 max-w-lg">
                {siteVisitData.pdfDocuments.map(pdf => (
                  <button 
                    key={pdf.driveFileId || pdf._id} 
                    onClick={(e) => {
                      e.stopPropagation();
                      import('@/lib/API').then(({ downloadFile }) => {
                        downloadFile(`${BASE_URL}${pdf.fileUrl}`, pdf.filename);
                      });
                    }}
                    className="flex items-center gap-2 text-[12px] text-blue-600 hover:underline hover:bg-slate-50 bg-white px-3 py-2.5 rounded-md border border-slate-200 shadow-sm transition-colors text-left w-full"
                  >
                    <FileText size={16} className="text-red-500 shrink-0" />
                    <span className="truncate">{pdf.filename}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Measurements Block */}
          {siteVisitData.rooms?.length > 0 ? (
            <div className="flex flex-col">
              <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
                Measurements & Requirements
              </h4>
              <div className="flex flex-col gap-4">
                {siteVisitData.rooms.map(room => {
                  const roomMeasurements = siteVisitData.measurements?.filter(m => m.room === room) || [];
                  if (roomMeasurements.length === 0) return null;
                  return (
                    <div key={room} className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
                        <h5 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">{room} ({roomMeasurements.length})</h5>
                      </div>
                      
                  <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[800px] table-fixed">
                          <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                              <th className="w-[12%] px-4 py-3 border border-slate-200 font-semibold">Zone</th>
                              <th className="w-[10%] px-4 py-3 border border-slate-200 font-semibold">Floor</th>
                              <th className="w-[20%] px-4 py-3 border border-slate-200 font-semibold">Requirement</th>
                              <th className="w-[6%] px-3 py-3 border border-slate-200 font-semibold text-center">H(mm)</th>
                              <th className="w-[6%] px-3 py-3 border border-slate-200 font-semibold text-center">W(mm)</th>
                              <th className="w-[6%] px-3 py-3 border border-slate-200 font-semibold text-center">D(mm)</th>
                              <th className="w-[5%] px-3 py-3 border border-slate-200 font-semibold text-center">Qty</th>
                              <th className="w-[20%] px-4 py-3 border border-slate-200 font-semibold">Notes</th>
                            </tr>
                          </thead>
                          <tbody className="text-[11px] text-slate-600">
                            {roomMeasurements.map((m, idx) => {
                              // Identify photos for this measurement
                              const photos = m.photos || [];
                              return (
                                <tr key={idx} className="hover:bg-slate-50/30 transition-colors group">
                                  <td className="px-4 py-3 border border-slate-200 font-medium text-slate-700 truncate max-w-[120px]" title={m.zone}>{m.zone || "—"}</td>
                                  <td className="px-4 py-3 border border-slate-200 truncate max-w-[120px]" title={m.floor}>{m.floor || "—"}</td>
                                  <td className="px-4 py-3 border border-slate-200 truncate max-w-[200px]" title={m.particulars}>{m.particulars || "—"}</td>
                                  <td className="px-3 py-3 border border-slate-200 text-center">{m.height || "—"}</td>
                                  <td className="px-3 py-3 border border-slate-200 text-center">{m.width || "—"}</td>
                                  <td className="px-3 py-3 border border-slate-200 text-center">{m.depth || "—"}</td>
                                  <td className="px-3 py-3 border border-slate-200 text-center font-medium">{m.qty || "—"}</td>
                                  <td className="px-4 py-3 border border-slate-200 italic text-slate-500 truncate max-w-[200px]" title={m.notes}>{m.notes || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-[#f8fafc] rounded-xl border border-slate-200 border-dashed py-12">
              <span className="text-[11px] font-medium text-slate-500 text-center px-4">No measurements available.</span>
            </div>
          )}
        </div>
      </DetailsWorkspaceModal>
      
      <SiteVisitMediaPreviewModal 
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        lead={lead}
        mediaList={allMedia.map(m => m.fileRaw)}
        initialMediaIndex={previewIndex}
      />
    </>
  );
};
